/* eslint-disable no-param-reassign */
import React from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@mui/material'
import clsx from 'clsx'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import TocIcon from '@mui/icons-material/Toc'
import { Form, Formik } from 'formik'
import moment from 'moment'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { withStyles } from '~/themes/useStyles'
import { MEDIA_FILE_TYPE, MEDIA_FILE_MODE } from '~/constants/media'
import { Field } from '~/components/Formik'
import Icon from '~/components/common/Icon'
import { COLORS } from '~/themes/common'
import Button from '~/components/common/Button'
import TimeRange from '~/components/common/TimeRange'

const style = (theme) => ({
  leftWrapper: {
    display: 'flex',
  },
  iconList: {
    padding: '2px 2px 0px 2px',
    borderRadius: 3,
    backgroundColor: '#EDF1FB',
    textAlign: 'center',
    marginLeft: 6,
    cursor: 'pointer',
    '&.disabled': {
      backgroundColor: '#F4F4F4',
    },
    '& > svg': {
      fontSize: 28,
    },
  },
  textSearch: {
    marginRight: theme.spacing(5),
    width: 270,
    '& > div.MuiInputBase-root': {
      paddingLeft: theme.spacing(1),
    },
    '@media screen and (max-width: 860px)': {
      width: 227,
    },
    '@media screen and (max-width: 646px)': {
      paddingLeft: theme.spacing(1),
    },
  },
  fileType: {
    '&.MuiOutlinedInput-root': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  placeholderType: {
    color: '#414141',
  },
  timePop: {
    '@media screen and (min-height: 200px) and (max-height: 706px)': {
      '& > div.MuiPaper-root': {
        overflow: 'auto',
      },
    },
  },
  ellipsisTimeRange: {
    '& input.MuiInputBase-input': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&::placeholder': {
        all: 'unset',
        color: '#414141',
        fontSize: 14,
        opacity: 1,
      },
    },
    '& div > div .MuiOutlinedInput-root': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  formWrapper: {
    '& > div': {
      '@media screen and (max-width: 1279px)': {
        width: '100%',
      },
      '&:first-of-type': {
        '@media screen and (min-height: 400px) and (max-height: 706px)': {
          padding: '2px 8px',
        },
      },
    },
  },
  leftContainer: {
    flexWrap: 'nowrap',
  },
  rightContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    margin: theme.spacing(-1, 0, -1),
    '@media screen and (min-width: 1280px) and (max-width: 1321px)': {
      width: 'calc(100% + 16px)',
    },
    '@media screen and (max-width: 860px)': {
      flexDirection: 'column',
      '& > div': {
        width: '100%',
      },
    },
  },
  optionWrapper: {
    width: '55%',
    paddingRight: '0 !important',
    '@media screen and (max-width: 1342px)': {
      paddingLeft: '0 !important',
    },
    '@media screen and (max-width: 1279px)': {
      paddingLeft: '6px !important',
    },
  },
  buttonContainer: {
    paddingRight: '0 !important',
    '@media screen and (max-width: 646px)': {
      paddingLeft: '6px !important',
    },
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    flexDirection: 'column',
    '@media screen and (max-width: 860px)': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    '@media screen and (max-width: 646px)': {
      '& button > span:first-of-type': {
        fontSize: 12,
      },
    },
  },
  optionContainer: {
    display: 'flex',
  },
  btnBulk: {
    marginTop: theme.spacing(1),
    '@media screen and (max-width: 860px)': {
      marginTop: 0,
    },
  },
})

const MEDIA_TYPE_LIST = [
  {
    value: MEDIA_FILE_TYPE.IMAGE,
    label: 'Photo',
  },
  {
    value: MEDIA_FILE_TYPE.VIDEO,
    label: 'Video',
  },
]

const Filter = ({
  classes,
  filters,
  mode,
  onChangeFileMode,
  onApplyFilter,
  onClearFilter,
  onBulkSelect,
  bulkSelect,
  galleryMode,
  showImage,
  showVideo,
}) => {
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down(1165))

  return (
    <div style={{ marginTop: 16, marginLeft: -8 }}>
      <Formik
        initialValues={filters}
        enableReinitialize
        onSubmit={onApplyFilter}
      >
        {({ values, setFieldValue, resetForm }) => (
          <Form>
            <Grid container spacing={2} className={classes.formWrapper}>
              <Grid lg={6} item>
                <Grid container spacing={2} className={classes.leftContainer}>
                  <Grid item lg={2}>
                    <div className={classes.leftWrapper}>
                      <div
                        className={clsx(
                          classes.iconList,
                          mode === MEDIA_FILE_MODE.LIST_VIEW && 'disabled',
                        )}
                        role="presentation"
                        onClick={() =>
                          onChangeFileMode(MEDIA_FILE_MODE.GRID_VIEW)
                        }
                      >
                        <ViewModuleIcon
                          color={
                            mode === MEDIA_FILE_MODE.LIST_VIEW
                              ? 'disabled'
                              : 'primary'
                          }
                        />
                      </div>
                      <div
                        className={clsx(
                          classes.iconList,
                          mode === MEDIA_FILE_MODE.GRID_VIEW && 'disabled',
                        )}
                        role="presentation"
                        onClick={() =>
                          onChangeFileMode(MEDIA_FILE_MODE.LIST_VIEW)
                        }
                      >
                        <TocIcon
                          color={
                            mode === MEDIA_FILE_MODE.GRID_VIEW
                              ? 'disabled'
                              : 'primary'
                          }
                        />
                      </div>
                    </div>
                  </Grid>
                  <Grid item lg={10}>
                    <Field.Input
                      containerClassName={classes.textSearch}
                      name="search_parameter"
                      placeholder="Find Media"
                      InputProps={{
                        startAdornment: (
                          <>
                            {!values.search_parameter && (
                              <Icon
                                name="search2"
                                color={COLORS.gray.textGray2}
                                alt="search"
                              />
                            )}
                          </>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid lg={6} item>
                <Grid container spacing={2} className={classes.rightContainer}>
                  {/* <Grid md={7} sm={7} lg={6} item> */}
                  <Grid item className={classes.optionWrapper}>
                    <div className={classes.optionContainer}>
                      <Field.Select
                        name="content_type"
                        disabled={showImage || showVideo}
                        placeholder="File Type"
                        options={MEDIA_TYPE_LIST}
                        clearable={!showImage && !showVideo}
                        classes={{
                          input: classes.fileType,
                          placeholder: classes.placeholderType,
                        }}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          getContentAnchorEl: null,
                        }}
                      />
                      <TimeRange
                        className={classes.ellipsisTimeRange}
                        classes={{
                          popover: classes.timePop,
                        }}
                        placeholder="Date Uploaded"
                        value={{
                          startDate: values.created_from
                            ? moment(values.created_from).format('MM/DD/YYYY')
                            : '',
                          endDate: values.created_to
                            ? moment(values.created_to).format('MM/DD/YYYY')
                            : '',
                        }}
                        onApply={({ startDate, endDate }) => {
                          setFieldValue(
                            'created_from',
                            startDate
                              ? moment(startDate, 'MM/DD/YYYY')
                                  .startOf('day')
                                  .format('YYYY-MM-DDTHH:mm:ss')
                              : '',
                          )
                          setFieldValue(
                            'created_to',
                            endDate
                              ? moment(endDate, 'MM/DD/YYYY')
                                  .endOf('day')
                                  .format('YYYY-MM-DDTHH:mm:ss')
                              : '',
                          )
                        }}
                        onClear={() => {
                          setFieldValue('created_from', '')
                          setFieldValue('created_to', '')
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid item className={classes.buttonContainer}>
                    <div className={classes.buttonWrapper}>
                      <div>
                        <Button type="submit">Apply</Button>
                        <Button
                          style={{ marginLeft: 10 }}
                          color="secondary"
                          onClick={() => {
                            resetForm()
                            onClearFilter()
                          }}
                        >
                          Clear All
                        </Button>
                      </div>
                      {isTablet && !galleryMode && (
                        <Button
                          color="secondary"
                          onClick={() => onBulkSelect(bulkSelect)}
                          className={classes.btnBulk}
                        >
                          {bulkSelect ? 'Cancel' : 'Bulk Select'}
                        </Button>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  )
}

Filter.defaultProps = {
  onBulkSelect: () => {},
  bulkSelect: false,
  galleryMode: false,
  showImage: false,
  showVideo: false,
}

Filter.propTypes = {
  classes: PropTypes.shape().isRequired,
  onApplyFilter: PropTypes.func.isRequired,
  onClearFilter: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  onChangeFileMode: PropTypes.func.isRequired,
  filters: PropTypes.oneOfType([PropTypes.object, PropTypes.shape]).isRequired,
  onBulkSelect: PropTypes.func,
  bulkSelect: PropTypes.bool,
  galleryMode: PropTypes.bool,
  showVideo: PropTypes.bool,
  showImage: PropTypes.bool,
}

export default withStyles(style)(Filter)
